import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { setDialogOpen } from '../../store/Actions';
import { buildDetailPath, useAppNavigate } from '../../utils/Routing';
import '../entityView/EntityView.scss';

const TagCard = ({tag, eventCount, onOpen, delay}) => {
  return (
    <button
      type="button"
      className="entity-view__card entity-view__card--tag"
      style={{'--card-delay': `${delay}ms`, '--card-accent': '#ec4899'}}
      onClick={onOpen}
    >
      <span className="entity-view__tag-hash" aria-hidden="true">#</span>
      <div className="entity-view__tag-name">{tag.name}</div>
      <div className="entity-view__card-meta">
        <span className="entity-view__card-count-pill">
          {eventCount} {eventCount === 1 ? 'event' : 'events'}
        </span>
      </div>
    </button>
  );
};

export function TagsView() {
  const user = useSelector(state => state.loggedInUser);
  const tags = useSelector(state => state.viewingUserTags) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const tagList = useMemo(() => Object.values(tags), [tags]);

  // Per-tag event counts so we can sort by usage and surface a top stat.
  const eventCountByTag = useMemo(() => {
    const counts = {};
    Object.values(events).forEach(event => {
      (event.tagIds || []).forEach(id => {
        counts[id] = (counts[id] || 0) + 1;
      });
    });
    return counts;
  }, [events]);

  const sortedTags = useMemo(() => {
    return [...tagList].sort((a, b) => {
      const diff = (eventCountByTag[b.id] || 0) - (eventCountByTag[a.id] || 0);
      if(diff !== 0) return diff;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [tagList, eventCountByTag]);

  const totalTagged = sortedTags.reduce(
    (sum, tag) => sum + (eventCountByTag[tag.id] || 0),
    0
  );

  const ownerName = viewingUserAccount?.displayName || viewingUserAccount?.name || 'User';
  const heroTitle = isOwnMap ? 'My Tags' : `${ownerName}'s Tags`;
  const heroSubtitle = tagList.length > 0
    ? `${tagList.length} ${tagList.length === 1 ? 'label' : 'labels'} for finding things later`
    : 'Add tags to organize and search your memories';

  const username = viewingUserAccount?.username;
  const openTag = (tag) => {
    if(!username) return;
    navigateTo(buildDetailPath(username, 'Tags', tag.id));
  };

  const openAddTagDialog = () => {
    dispatch(setDialogOpen('Edit Tag', {
      parentDialog: '',
      dialogTitle: 'Add Tag',
      tag: {
        userId: user?.uid,
        name: '',
      },
    }));
  };

  return (
    <main className="entity-view">
      <section className="entity-view__hero">
        <div className="entity-view__hero-glow entity-view__hero-glow--tags" aria-hidden="true"/>
        <div className="entity-view__hero-content">
          <div className="entity-view__hero-icon" aria-hidden="true">🏷️</div>
          <h1 className="entity-view__hero-title">{heroTitle}</h1>
          <div className="entity-view__hero-tagline">Tags</div>
          <div className="entity-view__hero-subtitle">{heroSubtitle}</div>
          {isOwnMap &&
            <div className="entity-view__hero-actions">
              <button
                type="button"
                className="entity-view__hero-add-btn"
                onClick={openAddTagDialog}
              >
                <AddIcon/>
                <span>Add Tag</span>
              </button>
            </div>
          }
        </div>
      </section>

      {tagList.length > 0 &&
        <section className="entity-view__stats-strip" aria-label="Tag stats">
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '120ms', '--stat-accent': '#ec4899'}}
          >
            <div className="entity-view__stat-value">{tagList.length}</div>
            <div className="entity-view__stat-label">Tags</div>
          </div>
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '200ms', '--stat-accent': '#8b5cf6'}}
          >
            <div className="entity-view__stat-value">{totalTagged}</div>
            <div className="entity-view__stat-label">Taggings</div>
          </div>
          {sortedTags[0] &&
            <div
              className="entity-view__stat"
              style={{'--stat-delay': '280ms', '--stat-accent': '#6366f1'}}
            >
              <div className="entity-view__stat-value" style={{fontSize:18, lineHeight:1.2}}>
                #{sortedTags[0].name}
              </div>
              <div className="entity-view__stat-label">Most Used</div>
            </div>
          }
        </section>
      }

      {tagList.length > 0 &&
        <section className="entity-view__panel">
          <header className="entity-view__panel-header">
            <h2>All Tags</h2>
            <span className="entity-view__panel-header-count">
              {tagList.length} total
            </span>
          </header>
          <div className="entity-view__grid entity-view__grid--tags">
            {sortedTags.map((tag, idx) => (
              <TagCard
                key={tag.id}
                tag={tag}
                eventCount={eventCountByTag[tag.id] || 0}
                onOpen={() => openTag(tag)}
                delay={120 + idx * 40}
              />
            ))}
          </div>
        </section>
      }

      {tagList.length === 0 &&
        <section className="entity-view__empty">
          <div className="entity-view__empty-icon" aria-hidden="true">🏷️</div>
          <div className="entity-view__empty-title">No tags yet</div>
          <div className="entity-view__empty-text">
            Tags are a flexible way to label events — try one for a hobby, a season, or a recurring theme.
          </div>
          {isOwnMap &&
            <button
              type="button"
              className="entity-view__hero-add-btn"
              onClick={openAddTagDialog}
            >
              <AddIcon/>
              <span>Add your first tag</span>
            </button>
          }
        </section>
      }
    </main>
  );
}
